import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { usePerformanceReportingContext } from '../PerformanceReportingContext';
import useMetrics from '../_data_sources/useMetrics';
import { PerformanceMetric, TrendValue } from '../_types';
import { ChartInfo } from '_lex/_atoms/ChartInfo';

type ChartInfoTrend = 'increasing' | 'neutral' | 'decreasing';

const mapTrendValueToChartInfoTrend = (trend: TrendValue | null): ChartInfoTrend | undefined => {
  if (!trend) return undefined;
  switch (trend) {
    case 'UP':
      return 'increasing';
    case 'DOWN':
      return 'decreasing';
    case 'NEUTRAL':
    case 'FLAT':
    case 'UNKNOWN':
    default:
      return 'neutral';
  }
};

const formatMetricValue = (metric: PerformanceMetric): number => {
  return metric.value;
};

const getColumnCount = (metricsLength: number): number => {
  // 2 or 4 metrics → 2 columns
  if (metricsLength === 2 || metricsLength === 4) {
    return 2;
  }
  // 3, 5, or 6 metrics → 3 columns
  if (metricsLength === 3 || metricsLength === 5 || metricsLength === 6) {
    return 3;
  }
  // Default to 3 columns for other cases
  return 3;
};

export function MetricsCards() {
  const { orgId, childOrgId, queryParams } = usePerformanceReportingContext();
  const { data: metrics, isLoading } = useMetrics({
    orgId,
    childOrgId,
    assignedModule: queryParams.assignedModule,
    startDate: queryParams.startDate,
    endDate: queryParams.endDate,
  });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!metrics || metrics.length === 0) {
    return null;
  }

  const columnCount = getColumnCount(metrics.length);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(columnCount, 2)}, 1fr)`,
          lg: `repeat(${columnCount}, 1fr)`,
        },
        gap: 4,
      }}
    >
      {metrics.map((metric, index) => (
        <ChartInfo
          key={`metric-card-${metric.key}-${index}`}
          title={metric.label}
          metric={formatMetricValue(metric)}
          metricFormat={metric.valueFormat}
          trend={mapTrendValueToChartInfoTrend(metric.trend)}
          color='neutral'
          tooltip={metric.description}
        />
      ))}
    </Box>
  );
}

export default MetricsCards;
